import { useEffect, useState } from 'react';

import { useAuth0 } from '@auth0/auth0-react';
import * as Sentry from '@sentry/react';
import { retryExchange } from '@urql/exchange-retry';
import { createClient as createWSClient } from 'graphql-ws';
import {
  Client,
  CombinedError,
  Provider,
  cacheExchange,
  fetchExchange,
  mapExchange,
  subscriptionExchange,
} from 'urql';

import {
  HasuraCustomClaims,
  HasuraCustomClaimsNamespace,
} from '@/components/common/AuthProvider';

const {
  VITE_HASURA_GRAPHQL_ENDPOINT_URL,
  VITE_HASURA_GRAPHQL_WS_ENDPOINT_URL,
  VITE_AUTH0_AUDIENCE,
} = import.meta.env;

export type TokenEndpointResponse = {
  id_token: string;
  access_token: string;
  refresh_token?: string;
  expires_in: number;
  scope?: string;
};

// subscription client
const wsClient = createWSClient({
  url: VITE_HASURA_GRAPHQL_WS_ENDPOINT_URL,
});

// retry config
const retryExchangeConfig = {
  initialDelayMs: 1000,
  maxDelayMs: 15000,
  randomDelay: true,
  maxNumberAttempts: 2,
  retryIf: (err: CombinedError) => Boolean(err && err.networkError),
};

// main client
const urqlClient = (
  accessToken?: TokenEndpointResponse,
  claims?: HasuraCustomClaims,
) =>
  new Client({
    url: VITE_HASURA_GRAPHQL_ENDPOINT_URL,
    exchanges: [
      mapExchange({
        onError(error: CombinedError, operation) {
          console.error(
            `GraphQL Error: Operation ${operation.key}`,
            error,
            operation,
          );
          Sentry.captureException(error);
        },
      }),
      cacheExchange,
      retryExchange(retryExchangeConfig),
      fetchExchange,
      subscriptionExchange({
        forwardSubscription(request) {
          const input = { ...request, query: request.query || '' };
          return {
            subscribe(sink) {
              const unsubscribe = wsClient.subscribe(input, sink);
              return { unsubscribe };
            },
          };
        },
      }),
    ],
    fetchOptions: () => {
      const headers: HeadersInit = {
        'x-hasura-role': 'public',
      };

      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken.id_token}`;
      }

      if (claims) {
        const allowedRoles = claims['x-hasura-allowed-roles'];
        // for now we support only one role but if we switch to multiple roles, always prefer admin if its present
        const userRole = allowedRoles.includes('admin')
          ? 'admin'
          : allowedRoles[0];
        headers['x-hasura-role'] = userRole;
        headers['x-hasura-user-id'] = claims['x-hasura-user-id'];
        headers['x-hasura-user-email'] = claims['x-hasura-user-email'];
        headers['x-hasura-user-company'] = claims['x-hasura-user-company'];
      }

      return {
        headers,
      };
    },
  });

function URQLProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user, getAccessTokenSilently } = useAuth0();

  const [client, setClient] = useState<Client>(urqlClient());

  useEffect(() => {
    async function updateClient() {
      if (isAuthenticated && user) {
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: VITE_AUTH0_AUDIENCE,
            redirect_uri: window.location.origin,
          },
          detailedResponse: true,
        });
        const cli = urqlClient(token, user[HasuraCustomClaimsNamespace]);
        setClient(cli);
      }
    }
    updateClient();
  }, [getAccessTokenSilently, isAuthenticated, user]);

  return <Provider value={client}>{children}</Provider>;
}

export { URQLProvider };
